<template>
    <div class="h-full flex flex-col space-y-4">
        <div class="flex items-center justify-between space-x-2">
            <h1 class="text-2xl font-semibold text-gray-700">Users</h1>
            <div class="flex items-center space-x-2">
                <UInput
                    v-model="search"
                    type="text"
                    placeholder="Search..."
                    icon="i-ph-magnifying-glass-duotone"
                />
                <UButton @click="openCreate()" color="black" icon="i-ph-plus-bold">
                    Add User
                </UButton>
            </div>
        </div>

        <div class="flex-1 bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
            <UTable
                :rows="filteredUsers"
                :columns="columns"
                :loading="loading"
                :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                :empty-state="{ icon: 'i-ph-users-duotone', label: 'No users.' }"
                :ui="{ thead: 'sticky top-0 bg-white z-10 shadow-sm', td: { padding: 'py-3' } }"
                class="h-full"
            >
                <template #partner-data="{ row }">
                    <span>{{ partnerName(row.partner_id) }}</span>
                </template>
                <template #updated_at-data="{ row }">
                    <TableDateCell :date="row.updated_at" />
                </template>
            </UTable>
        </div>

        <OverlayPartnerCreatePartnerUser
            v-model:open="overlays.create"
            :partners="scopedPartners"
            @reload="fetchData"
        />
    </div>
</template>

<script setup lang="ts">
import { UserRole } from '~/types/user'

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.partner] })
useHead({ title: 'Users' })

interface PartnerUser {
    id: string
    name: string
    email: string
    partner_id?: string
    updated_at: string
}

const toast = useToast()
const { overview, scopedPartners, load, partnerName } = usePartnerOverview()

const loading = ref<boolean>(false)
const search = ref<string>('')
const users = ref<PartnerUser[]>([])

const overlays = ref({
    create: false,
})

const columns = [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'partner', label: 'Partner' },
    { key: 'updated_at', label: 'Updated' },
]

const filteredUsers = computed(() => {
    const q = search.value.trim().toLowerCase()
    return users.value.filter(u => !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q))
})

onMounted(async () => {
    const data = await load()
    if (!data.canManageDistributors) {
        navigateTo('/activations', { replace: true })
        return
    }
    await fetchData()
})

async function fetchData() {
    loading.value = true

    try {
        users.value = await $fetch<PartnerUser[]>('/api/partner/users')
    } catch (error: any) {
        toast.add({ title: 'Could not load users', description: error.data?.message, color: 'red' })
    }

    loading.value = false
}

function openCreate() {
    overlays.value.create = true
}
</script>
